import React, { useEffect, useState, useRef } from 'react';

import { wrapFunctionalComponent } from 'utils/framework';
import isFunction from 'utils/functions/isFunction';

import { Flex } from 'components/ui';
import TextInput from 'components/Inputs/TextInput/TextInput';
import { colors, fontSizes, fontWeights } from 'style/config';
import stringUtils from 'utils/String';

function OtpInputGroup({
    otpLength = 6, onComplete, error = false, disabled = false, clearError, localization
}) {
    const [otpCode, setOtpCode] = useState(Array(otpLength).fill(''));
    const [hasError, setHasError] = useState(false);

    const inputRefs = useRef([]);

    const handleInputChange = (e, index) => {
        setHasError(false);
        isFunction(clearError) && clearError();

        const value = e.target.value;

        // Only allow a single digit input
        if (value.match(/^\d$/) || value === '') {
            const newOtp = [...otpCode];
            newOtp[index] = value;
            setOtpCode(newOtp);

            // Focus management (e.g., move to next input)
            if (value && index < otpLength - 1) {
                inputRefs.current[index + 1].focus();
            }
        } else {
            inputRefs.current[index].empty();
        }
    };

    const handleKeyDown = (e, index) => {
        // Move focus to the previous input on Backspace if the current input is empty
        if (e.key === 'Backspace' && otpCode[index] === '' && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handlePaste = e => {
        e.preventDefault();

        const pastedText = e.clipboardData.getData('text');
        const pastedTextToOtpLength = pastedText.slice(0, otpLength).split('');
        const isValidPaste = pastedTextToOtpLength.every(char => char.match(/^\d$/));

        if (isValidPaste) {
            setHasError(false);

            const newOtp = [...otpCode];

            for (let i = 0; i < otpLength; i++) {
                newOtp[i] = pastedTextToOtpLength[i] ?? '';
            }

            setOtpCode(newOtp);

            // Focus on the last filled input or the end
            if (pastedTextToOtpLength.length > 0) {
                inputRefs.current[pastedTextToOtpLength.length - 1].focus();
            }
        }
    };

    // Call onComplete if all digits are entered
    useEffect(() => {
        if (otpCode.every(digit => digit !== '') && isFunction(onComplete)) {
            onComplete(otpCode.join(''));
        }
    }, [otpCode, onComplete]);

    // Clear inputs on error
    useEffect(() => {
        if (error) {
            setHasError(true);
            setOtpCode(Array(otpLength).fill(''));
            inputRefs.current[0].focus();
        } else {
            setHasError(false);
        }
    }, [error]);

    // Focus on the first input on mount
    useEffect(() => {
        if (inputRefs.current) {
            inputRefs.current[0].focus();
        }
    }, []);

    return (
        <Flex
            is='fieldset'
            justifyContent='center'
            gap={4}
            onPaste={handlePaste}
        >
            {otpCode.map((digit, index) => (
                <TextInput
                    key={index}
                    maxLength={1}
                    autoOff={true}
                    value={digit}
                    onChange={e => handleInputChange(e, index)}
                    onKeyDown={e => handleKeyDown(e, index)}
                    ref={el => (inputRefs.current[index] = el)}
                    height={44}
                    marginBottom={0}
                    disabled={disabled}
                    aria-label={stringUtils.format(localization.accessibilityOtpInput, index + 1)}
                    customStyle={{
                        input: {
                            textAlign: 'center',
                            paddingLeft: 0,
                            paddingRight: 0,
                            minWidth: 44,
                            fontWeight: fontWeights.bold,
                            fontSize: fontSizes.md,
                            margin: 0
                        },
                        innerWrap: {
                            maxWidth: 44,
                            ...(hasError && { borderColor: colors.error })
                        }
                    }}
                />
            ))}
        </Flex>
    );
}

export default wrapFunctionalComponent(OtpInputGroup, 'OtpInputGroup');
